import * as React from 'react';
import {
  compose,
  withPropsOnChange,
  withHandlers,
  withState,
  lifecycle,
  withProps,
  branch,
  renderComponent
} from 'recompose';
import { reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Prompt, withRouter } from 'react-router';
import * as uuid from 'uuid/v4';
import { compact, get } from 'lodash';
import decode from 'jwt-decode';
import { Card } from 'material-ui/Card';

import { breadcrumbs } from '@inqool/uas-web-core/es/utils';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import * as storage from '@inqool/uas-web-core/es/utils/storage';

import { getConfigurationContext } from '@components/provider/configuration/ConfigurationProvider';
import HelpTooltip from '@components/HelpTooltip/HelpTooltip';
import { withNotifications } from '@components/hoc/withNotifications';
import { Tabs } from '@components/tabs';

import {
  getEvent,
  putEvent,
  lockEvent,
  unlockEvent,
  getSyncedFiles,
  getEventDefaultPlace,
  reserveEvent,
  releaseEvent,
  forceReleaseEvent
} from '@modules/events/_eventsActions';
import EventsFormReports from '@modules/events/EventsFormReports';
import EventsFormParticipants from '@modules/events/EventsFormParticipants';
import EventsFormDelicts from '@modules/events/EventsFormDelicts';
import EventsFormDescription from '@modules/events/EventsFormDescription';
import EventsFormFiles from '@modules/events/EventsFormFiles';
import EventsFormPhotos from '@modules/events/EventsFormPhotos';
import EventsFormNote from '@modules/events/EventsFormNote';
import EventsFormCorrespodences from '@modules/events/EventsFormCorrespodences';
import EventsFormHistories from '@modules/events/EventsFormHistories';
import EventsFormRecordServices, { WithoutId } from '@modules/events/EventsFormRecordServices';

import * as T from '@modules/events/_eventsTypes';
import { getActualDate, hasAnyPermission } from '@utils/utils';
import { Loader } from '@components/loader';
import { Row, Col } from 'react-flexbox-grid';
import { EventsFormBasic } from './EventsFormBasic';

const noop = () => {
  return true;
};

const enhance = compose<T.EventsFormProps, T.Outter & T.EventsFormProps>(
  withNotifications({
    getEvent,
    putEvent,
    lockEvent,
    unlockEvent,
    reserveEvent,
    releaseEvent,
    forceReleaseEvent
  }),
  withRouter,
  withState<{}, boolean, string, string>('loader', 'handleLoader', false),
  withState<{}, any, string, string>('activeTab', 'setActiveTab', 'events'),
  withState<{}, any, string, string>('disableSps', 'setDisableSps', false),
  withState<{}, any, string, string>('eventDefaultPlace', 'handleEventDefaultPlace', null),
  withState<{}, {}, string, string>('lockedUser', 'handleLockedUser', {}),
  withProps(
    ({
      getEventNotify,
      reserveEventNotify,
      releaseEventNotify,
      forceReleaseEventNotify,
      handleLockedUser,
      lockedUser
    }) => {
      const context = getConfigurationContext();
      const withLocks = get(context, 'config.extensions.locks') === 'true';
      let lockedEvent = false;
      let myId;
      if (withLocks) {
        const token = storage.get('token');
        const { id } = decode(token);
        myId = id;
        lockedEvent = id && lockedUser.id && id !== lockedUser.id;
      }
      return {
        context,
        withLocks,
        getEventNotify: getEventNotify(handleLockedUser),
        reserveEventNotify: withLocks ? reserveEventNotify(handleLockedUser) : noop,
        releaseEventNotify: withLocks ? releaseEventNotify : noop,
        forceReleaseEventNotify: withLocks ? forceReleaseEventNotify(handleLockedUser) : noop,
        lockedEvent,
        myId
      };
    }
  ),

  // Custom entityDetailEnhancer
  withState<{}, boolean, string, string>('fetchingEntity', 'toggleFetchingEntity', true),
  lifecycle<any, {}, {}>({
    async componentDidMount() {
      const { item, toggleFetchingEntity, getEventNotify } = this.props;
      if (item) {
        let entity;
        try {
          entity = await getEventNotify(item);
          const files = await getSyncedFiles(item);
          entity = {
            ...entity,
            files: get(entity, 'files', []).map(file => ({
              ...file,
              proceedingNumber: get(
                files.items.find(sync => get(sync, 'file.id', -1) === get(file, 'id', -2)),
                'proceedingNumber',
                null
              )
            }))
          };

          this.setState({ entity });
        } catch {
          this.setState({ entity: {} });
        }
      }
      toggleFetchingEntity(false);
    }
  }),
  branch(({ fetchingEntity }) => fetchingEntity, renderComponent(Loader)),
  // end of custom entityDetailEnhancer

  withPropsOnChange<{}, any>(['entity'], ({ entity = {} }) => ({
    initialValues: {
      classifications: [],
      correspondences: [],
      files: [],
      histories: [],
      officers: [],
      participants: [],
      delicts: [],
      photos: [],
      vehicles: [],
      weapons: [],
      person: get(entity, 'report.externalReporter'),
      eventDate: getActualDate(),
      report: { datetime: getActualDate(), internalReporters: [] },
      state: 'OPEN',
      ...entity
    },
    lockPermission: hasAnyPermission(['ROLE_EVENT_LOCK']),
    unlockPermission: hasAnyPermission(['ROLE_EVENT_UNLOCK']),
    unlockEventPermission: hasAnyPermission(['ROLE_EVENT_MANUAL_UNLOCK']),
    spsPermission: hasAnyPermission(['ROLE_SPS_LIST'])
  })),
  withHandlers({
    updateActiveTab: ({ setActiveTab }) => value => {
      setActiveTab(value);
      const url = new URL(window.location.href);
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('activeTab', value);
      url.search = searchParams.toString();
      window.history.pushState({}, '', url.toString());
    },
    participantsClassTypeStringToObject: () => ({ participants = [], ...formData }) => ({
      ...formData,
      participants: participants.map(p => ({
        ...(p as object),
        classificationType: get(p, 'classificationType', []).map(c => ({
          id: c
        }))
      }))
    }),
    participantsClassTypeObjectToString: () => ({ participants = [], ...formData }) => ({
      ...formData,
      participants: participants.map(p => ({
        ...(p as object),
        classificationType: get(p, 'classificationType', [])
      }))
    }),
    handleFiles: () => async (id, files) => {
      if (!id) {
        return files;
      }
      const { items } = await getSyncedFiles(id);

      const newFiles = files.map(file => ({
        ...file,
        proceedingNumber: get(
          items.find(sync => {
            const syncId = get(sync, 'file.id', -1);
            const fileId = get(file, 'id', -2);

            return syncId === fileId;
          }),
          'proceedingNumber',
          null
        )
      }));
      return newFiles;
    }
  }),
  withPropsOnChange(
    ['initialValues', 'participantsClassTypeStringToObject'],
    ({ initialValues, participantsClassTypeStringToObject }) => ({
      initialValues: participantsClassTypeStringToObject(initialValues)
    })
  ),
  breadcrumbs(({ initialValues }) => [
    { label: 'Události', to: '/events' },
    { label: get(initialValues, 'number', 'Nová událost') }
  ]),
  reduxForm({
    form: 'module-events',
    keepDirtyOnReinitialize: true,
    enableReinitialize: true
  }),
  connect(state => ({
    id: formValueSelector('module-events')(state, 'id'),
    eventNumber: formValueSelector('module-events')(state, 'number'),
    lock: formValueSelector('module-events')(state, 'lock')
  })),
  withHandlers({
    reloadEvent: ({ id, initialize, getEventNotify, participantsClassTypeStringToObject, handleFiles }) => async () => {
      const newEvent = participantsClassTypeStringToObject(await getEventNotify(id));
      if (newEvent) {
        const files = await handleFiles(id, get(newEvent, 'files', []));
        initialize({
          ...newEvent,
          files,
          person: !!get(newEvent, 'report.externalReporter')
        });
        return newEvent;
      }
      return null;
    },
    onSubmitAndClose: ({
      putEventNotify,
      reserveEventNotify,
      closeItem,
      participantsClassTypeObjectToString,
      handleLoader,
      myId
    }) => async formData => {
      handleLoader(true);
      const body = { id: formData.id ? formData.id : uuid(), ...formData };
      const eventReserved = await reserveEventNotify(body.id, myId);
      let event;
      if (eventReserved) {
        event = await putEventNotify(participantsClassTypeObjectToString(body));
      }
      handleLoader(false);
      if (event) {
        closeItem();
      }
    },
    onSubmitAndReinitialize: ({
      history,
      handleLoader,
      initialize,
      putEventNotify,
      reserveEventNotify,
      participantsClassTypeObjectToString,
      participantsClassTypeStringToObject,
      setDisableSps,
      myId
    }) => async formData => {
      handleLoader(true);
      const body = { id: formData.id ? formData.id : uuid(), ...formData };
      let newEvent: object | null = null;
      try {
        const eventReserved = await reserveEventNotify(body.id, myId);
        if (eventReserved) {
          newEvent = participantsClassTypeStringToObject(
            await putEventNotify(participantsClassTypeObjectToString(body))
          );
        }
      } catch (e) {
        console.log(e);
      }
      if (newEvent) {
        initialize({
          ...newEvent,
          person: !!get(newEvent, 'report.externalReporter')
        });

        if (!get(formData, 'id')) {
          history.push(`/events?item=${get(newEvent, 'id')}`);
        }
      }
      setDisableSps(false);
      handleLoader(false);
    }
  }),
  withHandlers({
    handleLockEvent: ({ id, reloadEvent, lockEventNotify, handleLoader }) => async () => {
      handleLoader(true);
      await lockEventNotify(id);
      await reloadEvent();
      handleLoader(false);
    },
    handleUnlockEvent: ({ id, reloadEvent, unlockEventNotify, handleLoader }) => async () => {
      handleLoader(true);
      await unlockEventNotify(id);
      await reloadEvent();
      handleLoader(false);
    },
    handleReserveEvent: ({ id, myId, reserveEventNotify, handleLoader }) => async () => {
      handleLoader(true);
      await reserveEventNotify(id, myId, true);
      handleLoader(false);
    },
    handleReleaseEvent: ({ id, releaseEventNotify }) => () => {
      releaseEventNotify(id);
    },
    handleForceReleaseEvent: ({ id, forceReleaseEventNotify, handleLoader }) => async () => {
      handleLoader(true);
      await forceReleaseEventNotify(id);
      handleLoader(false);
    }
  }),
  lifecycle<any, any>({
    async componentDidMount() {
      const { handleEventDefaultPlace, handleLoader, setActiveTab } = this.props;
      handleLoader(true);
      const eventDefaultPlace = await getEventDefaultPlace();
      handleEventDefaultPlace(eventDefaultPlace);
      handleLoader(false);

      const searchParams = new URLSearchParams(window.location.search);

      const activeTab = searchParams.get('activeTab');

      if (activeTab) {
        setActiveTab(activeTab);
      }
    },
    componentWillUnmount() {
      if (this.props.handleReleaseEvent) {
        this.props.handleReleaseEvent();
      }
    }
  })
);

const View: React.SFC<T.Outter & T.Inner & T.EventsFormProps> = ({
  handleSubmit,
  reloadEvent,
  closeItem,
  dirty,
  submitSucceeded,
  submitting,
  change,
  id,
  eventNumber,
  onSubmitAndClose,
  onSubmitAndReinitialize,
  form,
  loader,
  initialize,
  lockPermission,
  unlockPermission,
  unlockEventPermission,
  spsPermission,
  lock,
  handleLockEvent,
  handleUnlockEvent,
  eventDefaultPlace,
  invalid,
  submitFailed,
  activeTab,
  updateActiveTab,
  disableSps,
  setDisableSps,
  context,
  lockedUser,
  lockedEvent,
  handleReserveEvent,
  handleForceReleaseEvent
}) => {
  const locked = lock || lockedEvent;
  const sps = get(context, 'config.extensions.sps');
  return (
    <div>
      <Prompt when={dirty && !submitSucceeded && !submitting} message="Vaše změny nebyly uloženy, přesto odejít?" />
      {loader && <Loader />}
      <form className="layout" onSubmit={handleSubmit}>
        <Card>
          <Tabs
            value={activeTab}
            onChange={value => updateActiveTab(value)}
            lazy={true}
            items={compact([
              {
                value: 'events',
                label: 'Událost',
                style: { minWidth: 175 },
                content: <EventsFormBasic change={change} disabled={locked} eventDefaultPlace={eventDefaultPlace} />
              },
              {
                value: 'reports',
                label: 'Oznamovatel',
                style: { minWidth: 175 },
                content: <EventsFormReports change={change} form={form} disabled={locked} />
              },
              {
                value: 'participants',
                label: 'Účastníci',
                style: { minWidth: 175 },
                content: <EventsFormParticipants disabled={locked} />
              },
              {
                value: 'delicts',
                label: 'Přestupky',
                style: { minWidth: 175 },
                content: (
                  <EventsFormDelicts
                    disabled={dirty || !id || locked}
                    locked={locked}
                    event={{ id, number: eventNumber }}
                    reloadEvent={reloadEvent}
                    eventDefaultPlace={eventDefaultPlace}
                    context={context}
                  />
                )
              },
              {
                value: 'description',
                label: 'Hlášení',
                style: { minWidth: 175 },
                content: (
                  <EventsFormDescription
                    disabled={dirty || !id || locked}
                    locked={locked}
                    eventId={id}
                    reloadEvent={reloadEvent}
                  />
                )
              },
              {
                value: 'files',
                label: 'Soubory',
                style: { minWidth: 175 },
                content: (
                  <EventsFormFiles
                    eventId={id}
                    reloadEvent={reloadEvent}
                    disabled={dirty || !id || locked}
                    locked={locked}
                    initialize={initialize}
                    setDisableSps={setDisableSps}
                  />
                )
              },
              sps &&
                spsPermission && {
                  value: 'recordServices',
                  label: 'Spisová služba',
                  style: { minWidth: 175 },
                  content:
                    id && !disableSps ? (
                      <EventsFormRecordServices
                        eventId={id}
                        reloadEvent={reloadEvent}
                        disabled={locked}
                        sps={sps}
                        activeTab={activeTab}
                      />
                    ) : (
                      <WithoutId />
                    )
                },
              {
                value: 'photos',
                label: 'Fotodokumentace',
                style: { minWidth: 175 },
                content: <EventsFormPhotos disabled={locked} setDisableSps={setDisableSps} />
              },
              {
                value: 'note',
                label: 'Poznámka',
                style: { minWidth: 175 },
                content: <EventsFormNote disabled={locked} />
              },
              {
                value: 'correspondences',
                label: 'Korespondence',
                style: { minWidth: 175 },
                content: <EventsFormCorrespodences disabled={locked} />
              },
              {
                value: 'histories',
                label: 'Historie',
                style: { minWidth: 175 },
                content: <EventsFormHistories disabled={locked} />
              }
            ])}
          />
          {lockedEvent && (
            <Row>
              <Col md={12}>
                <h4 style={{ marginLeft: '16px', color: 'red' }}>
                  {`Událost jíž edituje uživatel: ${lockedUser.fullName}`}
                </h4>
              </Col>
            </Row>
          )}
          {activeTab === 'recordServices' ? (
            <Button
              label="Uložit"
              disabled={!dirty}
              primary={true}
              style={{ margin: `16px 0 16px ${dirty || !id ? '0' : '16px'}` }}
              onClick={handleSubmit(onSubmitAndReinitialize)}
            />
          ) : locked ? (
            <>
              {lock &&
                unlockPermission && (
                  <Button
                    label="Odemknout"
                    primary={true}
                    style={{ margin: '16px 0 16px 16px' }}
                    onClick={handleUnlockEvent}
                  />
                )}
            </>
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
              {lockPermission && (
                <>
                  <Button
                    label="Uzamknout"
                    disabled={dirty || !id}
                    primary={true}
                    style={{ margin: '16px 0 16px 16px' }}
                    onClick={handleLockEvent}
                  />
                  {(dirty || !id) && (
                    <HelpTooltip help="Před uzamčením je potřeba uložit změny" tooltipPosition="top-right" />
                  )}
                </>
              )}
              {invalid &&
                submitFailed && (
                  <div style={{ color: '#f00', margin: '0 16px' }}>
                    Formulář není validní. Zkontrolujte všechny jeho části.
                  </div>
                )}
              <Button
                label="Uložit"
                primary={true}
                style={{ margin: `16px 0 16px ${dirty || !id ? '0' : '16px'}` }}
                onClick={handleSubmit(onSubmitAndReinitialize)}
              />
              <Button
                label="Uložit a zavřít"
                primary={true}
                style={{ margin: '16px 0 16px 16px' }}
                onClick={handleSubmit(onSubmitAndClose)}
              />
            </div>
          )}
          {lockedEvent &&
            unlockEventPermission && (
              <Button
                label="Uvolnit Událost"
                primary={true}
                style={{ margin: `16px 0 16px ${dirty || !id ? '0' : '16px'}` }}
                onClick={handleForceReleaseEvent}
              />
            )}
          {lockedEvent && (
            <Button
              label="Zažádat o rezervování události"
              primary={true}
              style={{ margin: `16px 0 16px ${dirty || !id ? '0' : '16px'}` }}
              onClick={handleReserveEvent}
            />
          )}
          <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} onClick={closeItem} />
        </Card>
      </form>
    </div>
  );
};

export const EventsForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsForm.tsx